import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { 
  Crown, 
  Check, 
  ArrowLeft, 
  Zap, 
  Image, 
  Video, 
  Code, 
  Bot,
  Star,
  Shield,
  Clock,
  Sparkles,
  Users,
  BarChart3,
  Headphones
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import axios from 'axios';
import PaymentModal from './PaymentModal';

const Premium = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [plans, setPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get('/api/premium/plans');
      setPlans(response.data);
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast.error('Planlar yüklenemedi');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubscribe = (plan) => {
    if (!user) {
      toast.error('Giriş yapmanız gerekiyor');
      navigate('/login');
      return;
    }

    setSelectedPlan(plan);
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = () => {
    updateUser({ ...user, is_premium: true });
    setShowPaymentModal(false);
    setSelectedPlan(null);
    navigate('/profile');
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(price);
  };

  const getPlanIcon = (planName) => {
    switch (planName) {
      case 'Basic':
        return <Zap className="w-8 h-8 text-blue-500" />;
      case 'Pro':
        return <Crown className="w-8 h-8 text-purple-500" />;
      case 'Enterprise':
        return <Star className="w-8 h-8 text-yellow-500" />;
      default:
        return <Bot className="w-8 h-8 text-gray-500" />;
    }
  };

  const getPlanColor = (planName) => {
    switch (planName) {
      case 'Basic':
        return 'from-blue-500 to-blue-600';
      case 'Pro':
        return 'from-purple-500 to-purple-600';
      case 'Enterprise':
        return 'from-yellow-500 to-yellow-600';
      default:
        return 'from-gray-500 to-gray-600';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 p-4">
        <div className="max-w-6xl mx-auto flex items-center space-x-4">
          <button
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-2xl font-bold text-white">Premium Özellikler</h1>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6">
        {/* Billing Cycle Toggle */}
        <div className="text-center mb-12">
          <div className="inline-flex bg-gray-800 rounded-lg p-1">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-md transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Aylık
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-6 py-2 rounded-md transition-all ${
                billingCycle === 'yearly'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Yıllık
              <span className="ml-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                %20 İndirim
              </span>
            </button>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`relative bg-gray-800 rounded-2xl p-8 border-2 transition-all duration-300 ${
                plan.name === 'Pro'
                  ? 'border-purple-500 scale-105'
                  : 'border-gray-700 hover:border-gray-600'
              }`}
            >
              {plan.name === 'Pro' && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                    En Popüler
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <div className="flex justify-center mb-4">
                  {getPlanIcon(plan.name)}
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                <p className="text-gray-400 mb-4">{plan.description}</p>
                
                <div className="mb-4">
                  <span className="text-4xl font-bold text-white">
                    {formatPrice(billingCycle === 'yearly' ? plan.yearly_price : plan.monthly_price)}
                  </span>
                  <span className="text-gray-400 ml-2">
                    /{billingCycle === 'yearly' ? 'yıl' : 'ay'}
                  </span>
                </div>

                {billingCycle === 'yearly' && (
                  <div className="text-green-400 text-sm font-semibold">
                    Aylık {formatPrice(plan.yearly_price / 12)} tasarruf!
                  </div>
                )}
              </div>

              {/* Features */}
              <div className="space-y-4 mb-8">
                {plan.features.map((feature, featureIndex) => (
                  <div key={featureIndex} className="flex items-center space-x-3">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-gray-300">{feature}</span>
                  </div>
                ))}
              </div>

              {/* Limits */}
              <div className="bg-gray-700 rounded-lg p-4 mb-6">
                <h4 className="text-white font-semibold mb-3">Limitler</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Günlük Mesaj:</span>
                    <span className="text-white">
                      {plan.max_messages_per_day === -1 ? 'Sınırsız' : plan.max_messages_per_day}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Görsel Oluşturma:</span>
                    <span className="text-white">
                      {plan.image_generation_limit === 0 ? 'Sınırsız' : `Aylık ${plan.image_generation_limit}`}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Video Oluşturma:</span>
                    <span className="text-white">
                      {plan.video_generation_limit === 0 ? 'Sınırsız' : `Aylık ${plan.video_generation_limit}`}
                    </span>
                  </div>
                </div>
              </div>

              {/* Subscribe Button */}
              <button
                onClick={() => handleSubscribe(plan)}
                disabled={user?.is_premium}
                className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200 ${
                  user?.is_premium
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : plan.name === 'Pro'
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600'
                    : `bg-gradient-to-r ${getPlanColor(plan.name)} text-white hover:opacity-90`
                }`}
              >
                {user?.is_premium ? (
                  'Zaten Premium'
                ) : (
                  'Abone Ol'
                )}
              </button>
            </motion.div>
          ))}
        </div>

        {/* Features Comparison */}
        <div className="mt-16">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            Premium Özellikler
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gray-800 rounded-xl p-6 text-center">
              <Bot className="w-12 h-12 text-blue-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Gelişmiş AI</h3>
              <p className="text-gray-400">GPT-4, Claude ve daha fazla gelişmiş model</p>
            </div>
            
            <div className="bg-gray-800 rounded-xl p-6 text-center">
              <Image className="w-12 h-12 text-green-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Görsel Üretimi</h3>
              <p className="text-gray-400">AI ile profesyonel görseller oluşturun</p>
            </div>
            
            <div className="bg-gray-800 rounded-xl p-6 text-center">
              <Video className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Video Üretimi</h3>
              <p className="text-gray-400">Metinlerden video oluşturun</p>
            </div>
            
            <div className="bg-gray-800 rounded-xl p-6 text-center">
              <Headphones className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Öncelikli Destek</h3>
              <p className="text-gray-400">7/24 öncelikli müşteri desteği</p>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-16">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            Sık Sorulan Sorular
          </h2>
          
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="bg-gray-800 rounded-xl p-6">
              <h3 className="text-xl font-semibold text-white mb-3">
                Aboneliğimi istediğim zaman iptal edebilir miyim?
              </h3>
              <p className="text-gray-400">
                Evet, aboneliğinizi istediğiniz zaman iptal edebilirsiniz. 
                İptal işlemi sonrası mevcut dönem sonuna kadar premium özelliklerinizi kullanmaya devam edebilirsiniz.
              </p>
            </div>
            
            <div className="bg-gray-800 rounded-xl p-6">
              <h3 className="text-xl font-semibold text-white mb-3">
                Yıllık abonelikte gerçekten tasarruf ediyor muyum?
              </h3>
              <p className="text-gray-400">
                Evet! Yıllık abonelikte aylık fiyatın %20'si kadar tasarruf edersiniz. 
                Bu, yıllık 2 aylık ücret tasarrufu anlamına gelir.
              </p>
            </div>
            
            <div className="bg-gray-800 rounded-xl p-6">
              <h3 className="text-xl font-semibold text-white mb-3">
                Hangi ödeme yöntemlerini kabul ediyorsunuz?
              </h3>
              <p className="text-gray-400">
                Kredi kartı, banka kartı ve PayPal ile ödeme yapabilirsiniz. 
                Tüm ödemeler güvenli SSL şifreleme ile korunur.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        plan={selectedPlan}
        billingCycle={billingCycle}
        onSuccess={handlePaymentSuccess}
      />
    </div>
  );
};

export default Premium;